import { Helmet } from 'react-helmet-async';
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';

import { useNavigate } from 'react-router-dom';
import { api } from 'src/api/api-client.config';
import SearchDialog from '../components/pickem-admin/SearchDialog';
import LeagueDialog from '../components/pickem-admin/LeagueDialog';
import { useGetFriendLeaguesQuery } from '@/api/leagues/getFriendLeagues.ts';
import { LeagueType } from '@/@types/api.types.ts';
import { useFindLeagueByTitleMutation } from '@/api/leagues/findLeagueByTitle.ts';
// ----------------------------------------------------------------------
export default function FriendsLeaguePage() {
  const navigate = useNavigate();
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [openLeagueDialog, setOpenLeagueDialog] = useState(false);
  const [editLeague, setEditLeague] = useState<LeagueType | null>(null);

  const { data: leagues = [], isLoading: isGetLeaguesLoading } =
    useGetFriendLeaguesQuery();

  const { mutateAsync: findLeague, data: searchLeagueResponse } =
    useFindLeagueByTitleMutation();

  const searchLeague = useMemo(
    () => searchLeagueResponse?.league[0] || null,
    [searchLeagueResponse]
  );

  const successLeagueSearchHandler = async (league: LeagueType) => {
    await findLeague(league.title);
  };

  const successLeagueEditHandler = (league: LeagueType) => {
    api
      .put(`/admin/leagues/${league.league_id}`, {
        ...league,
        league_type: 'FRIENDS',
        league_team_id: null,
      })
      .then(() => {
        window.location.reload();
      });
  };

  const closeLeagueDialog = () => {
    setEditLeague(null);
    setOpenLeagueDialog(false);
    setOpenSearchDialog(false);
  };

  const openSearchLeagueDialog = () => {
    setEditLeague(null);
    setOpenSearchDialog(true);
  };

  const openEditLeagueDialog = (index: number) => {
    const league = leagues[index];
    setEditLeague(league);
    setOpenLeagueDialog(true);
  };

  return (
    <>
      <Helmet>
        <title>Friends Leagues</title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Friends Leagues
          </Typography>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:search-fill' />}
            onClick={() => openSearchLeagueDialog()}
          >
            Search
          </Button>
        </Stack>

        <Card>
          {isGetLeaguesLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Image</TableCell>
                      <TableCell>Image Fr</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leagues.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component='th' scope='row'>
                          {row.league_id}
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          {row.title}
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          {row.league_type}
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          {row.code}
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          <img
                            style={{ width: 100 }}
                            src={row.league_image_en}
                          />
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          <img
                            style={{ width: 100 }}
                            src={row.league_image_fr}
                          />
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          <Iconify
                            icon={'eva:calendar-outline'}
                            sx={{ cursor: 'pointer' }}
                            onClick={() =>
                              navigate(
                                `/dashboard/league-tournaments?league_id=${row.league_id}`
                              )
                            }
                          />
                          <Iconify
                            icon={'eva:edit-outline'}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => openEditLeagueDialog(index)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>
      <SearchDialog
        league={searchLeague}
        open={openSearchDialog}
        onSuccessHandler={successLeagueSearchHandler}
        onClose={closeLeagueDialog}
      />
      <LeagueDialog
        league={editLeague!}
        open={openLeagueDialog}
        onSuccessHandler={successLeagueEditHandler}
        onClose={closeLeagueDialog}
        isSearch={true}
      />
    </>
  );
}
